import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Tabs,
  Tab,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  AppBar,
  Toolbar,
  Snackbar
} from '@material-ui/core';
import {
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Map as MapIcon,
  Timeline as TimelineIcon,
  NoteAdd as NoteIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@material-ui/icons';
import { Link } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles';
import axios from 'axios';
// import { isSameMonth, isSameYear, format } from 'date-fns';
import { format, subDays, addDays, isToday, isSameYear, isSameMonth } from 'date-fns';
import MapView from './MapView';
import WindDirectionIcon from '../../Common/WindDirectionIcon'

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(3),
  },
  dateNavigator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  dateDisplay: {
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
  },
  calendarIcon: {
    marginRight: theme.spacing(1),
  },
  jobCard: {
    height: '100%',
    transition: 'transform 0.2s ease-in-out',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[4],
    },
  },
  jobCardContent: {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  jobTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  jobSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  jobStatus: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  completedByChip: {
    backgroundColor: theme.palette.info.main,
    color: 'white',
    fontWeight: 500,
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '0.75rem',
  },
  completedChip: {
    backgroundColor: theme.palette.success.main,
    color: 'white',
    fontWeight: 500,
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '0.75rem',
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
  },
  dialogContent: {
    padding: theme.spacing(2),
  },
  mapContainer: {
    height: 400,
    width: '100%',
    backgroundColor: '#e0e0e0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  mapPlaceholder: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  detailsContainer: {
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
  },
  infoItem: {
    padding: theme.spacing(1, 0),
  },
  infoLabel: {
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  infoValue: {
    marginTop: theme.spacing(0.5),
  },
  tabsContainer: {
    marginBottom: theme.spacing(2),
  },
  weatherInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  weatherItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  noJobs: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  navButton: {
    minWidth: 'auto',
  },
  locationInfo: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  chemicalItem: {
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  dialogLarge: {
    '& .MuiDialog-paper': {
      minWidth: '80%',
      maxWidth: '95%',
      maxHeight: '90vh',
    },
  },
  fullWidthTab: {
    width: '100%',
  }
}));

// Tab Panel Component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`job-detail-tabpanel-${index}`}
      aria-labelledby={`job-detail-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={2}>{children}</Box>}
    </div>
  );
}

const DashboardHeader = ({ data, url, chemicals }) => {
  const classes = useStyles();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [activeSprayLog, setActiveSprayLog] = useState({})
  const [filteredJobs, setFilteredJobs] = useState([])
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'success' });
  const [propertyLines, setPropertyLines] = useState([])
  const [gpsData, setGPSData] = useState([])
  const [woNotes, setWoNotes] = useState([])
  // Fetch jobs for selected date
  const fetchJobs = async () => {
    setLoading(true);
    try {
      // Format date for API
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      
      // Replace with your actual API endpoint
      const response = await axios.get(`${url}/api/hmi/genericGetRequest`, {
        params: {
          type: 'COMPLETED_SPRAY_JOBS',
          date: formattedDate
        }
      });
      console.log('COMPLETED_SPRAY_JOBS Jobs...', response.data)
      // Set jobs from response
      if (response && response.data && response.data.data) {
       console.log('set jobs...', response.data.data)
        setJobs(response.data.data || []);
        console.log('Selected Date', selectedDate)
        setTimeout(() => {
          filterOurJobs(response.data.data)
        }, 100)
        // let jobsForToday = [...response.data.data].filter(wo => {
        //   console.log('Wo - completed on??', wo)
        //   const completedDate = wo.work_complete_at
        //   console.log('CompletedDat', completedDate)
        //   // const formattedCmpDate = format(completedDate, 'yyyy-MM-dd');
        //   // console.log('formattedCmpDate: %s . . formattedDate: %s ', formattedCmpDate, formattedDate)
        // }
        //   )
        //   console.log('jobsForToday', jobsForToday)
        // // setFilteredJobs(jobsForToday)
      } else {
        setJobs([]);
      }
    } catch (error) {
      console.error('Error fetching completed jobs:', error);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {


    fetchJobs();
  }, [url]);

  const filterOurJobs = (passedJobs = null) => {
    const formattedDate = format(selectedDate, 'yyyy-MM-dd');
    // console.log('Formatted Date', formattedDate)
    let jobsToUse = passedJobs ? passedJobs : [...jobs]
    // console.log('Jobs to use:', jobsToUse)
    let jobsForToday = jobsToUse.filter(wo => {
      // console.log('Wo - completed on??', wo)
      const completedDate = new Date(wo.work_complete_at)
      // console.log('CompletedDat', completedDate)
      const formattedCmpDate = format(completedDate, 'yyyy-MM-dd');
      // console.log('formattedCmpDate: %s . . formattedDate: %s ', formattedCmpDate, formattedDate)
      if (formattedCmpDate === formattedDate) return true
      return false
    })
      // console.log('jobsForToday', jobsForToday)
      setFilteredJobs(jobsForToday)
  }

 
  useEffect(() => {
    console.log('Selected Date changed', selectedDate);

    if (!jobs || jobs.length === 0) {
      console.log('No jobs yet, fetching...');
      fetchJobs(selectedDate);
      return;
    }

    // Check if selectedDate is in the same month as the jobs data
    const firstJobDate = new Date(jobs[0].work_complete_at);
    const lastJobDate = new Date(jobs[jobs.length - 1].work_complete_at);

    const selectedMonth = selectedDate.getMonth();
    const selectedYear = selectedDate.getFullYear();

    const firstMonth = firstJobDate.getMonth();
    const firstYear = firstJobDate.getFullYear();

    const lastMonth = lastJobDate.getMonth();
    const lastYear = lastJobDate.getFullYear();

    const isInSameMonthAsAnyJob =
      (isSameMonth(selectedDate, firstJobDate) && isSameYear(selectedDate, firstJobDate)) ||
      (isSameMonth(selectedDate, lastJobDate) && isSameYear(selectedDate, lastJobDate));

    if (!isInSameMonthAsAnyJob) {
      console.log('Different month selected, fetching new jobs...');
      fetchJobs(selectedDate);
      return;
    }

    filterOurJobs();
  }, [selectedDate]);



  const showSnackbar = useCallback((message, type = 'success') => {
    setSnackbar({ open: true, message, type });
}, []);

const hideSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
}, []);
  // Mock data for development
  // useEffect(() => {
  //   // This is just for development, remove in production
  //   const mockJobs = [
  //     {
  //       _id: '1',
  //       jobsiteName: 'Lakeside Park',
  //       address: '123 Lake View Dr',
  //       bizName: 'City Parks Department',
  //       startTime: '2024-04-18T08:30:00Z',
  //       stopTime: '2024-04-18T11:45:00Z',
  //       techName: 'Robert Johnson',
  //       licenseNum: 'TX54321',
  //       weather: {
  //         temp: '72°F',
  //         windSpeed: '5 mph',
  //         windDir: 'NW',
  //         conditions: 'Sunny'
  //       },
  //       location: {
  //         start: { lat: 32.7767, lng: -96.7970 },
  //         stop: { lat: 32.7780, lng: -96.7965 },
  //         perimeter: [
  //           { lat: 32.7767, lng: -96.7970 },
  //           { lat: 32.7780, lng: -96.7965 },
  //           { lat: 32.7775, lng: -96.7950 },
  //           { lat: 32.7760, lng: -96.7955 }
  //         ],
  //         sprayAreas: [
  //           [
  //             { lat: 32.7767, lng: -96.7960 },
  //             { lat: 32.7770, lng: -96.7955 },
  //             { lat: 32.7765, lng: -96.7950 }
  //           ]
  //         ]
  //       },
  //       chemicalsUsed: [
  //         { name: 'RoundUp Pro', amount: '2.5 gallons', concentration: '2%' },
  //         { name: 'Surfactant', amount: '8 oz', concentration: '0.5%' }
  //       ],
  //       notes: 'Treated invasive species along the eastern perimeter. Follow-up needed in 2 weeks.'
  //     },
  //     {
  //       _id: '2',
  //       jobsiteName: 'Downtown Office Plaza',
  //       address: '500 Main Street',
  //       bizName: 'Urban Properties LLC',
  //       startTime: '2024-04-18T13:15:00Z',
  //       stopTime: '2024-04-18T15:30:00Z',
  //       techName: 'Robert Johnson',
  //       licenseNum: 'TX54321',
  //       weather: {
  //         temp: '76°F',
  //         windSpeed: '3 mph',
  //         windDir: 'SE',
  //         conditions: 'Partly Cloudy'
  //       },
  //       location: {
  //         start: { lat: 32.7800, lng: -96.8000 },
  //         stop: { lat: 32.7810, lng: -96.8010 },
  //         perimeter: [
  //           { lat: 32.7800, lng: -96.8000 },
  //           { lat: 32.7810, lng: -96.8000 },
  //           { lat: 32.7810, lng: -96.8010 },
  //           { lat: 32.7800, lng: -96.8010 }
  //         ],
  //         sprayAreas: [
  //           [
  //             { lat: 32.7802, lng: -96.8002 },
  //             { lat: 32.7808, lng: -96.8002 },
  //             { lat: 32.7808, lng: -96.8008 },
  //             { lat: 32.7802, lng: -96.8008 }
  //           ]
  //         ]
  //       },
  //       chemicalsUsed: [
  //         { name: 'EcoFriendly Weed Control', amount: '3 gallons', concentration: '1.5%' }
  //       ],
  //       notes: 'Treated landscaped areas around building perimeter. Client requested organic options only.'
  //     }
  //   ];
    
  //   // Only use mock data if in development mode
  //   if (process.env.NODE_ENV === 'development' && jobs.length === 0 && !loading) {
  //     setJobs(mockJobs);
  //   }
  // }, [jobs.length, loading]);

  const handlePreviousDay = () => {
    setSelectedDate(prevDate => subDays(prevDate, 1));
  };

  const handleNextDay = () => {
    setSelectedDate(prevDate => addDays(prevDate, 1));
  };

  const fetchWorkorderNotes = async (job) => {
    try {
      const response = await axios.get(`${url}/api/hmi/genericGetRequest`, {
        params: {
          type: 'WO_NOTES',
          workorderId: job._id
        }
      });
      if (response.data?.length) {
        // console.log('Got workorder notes...', response.data)
        // setActiveSprayLog(response.data[0])
        setWoNotes(response.data)
      } else {
        setWoNotes([])
      }
    } catch (e) {
      console.log('Error getting workorder notes......', e)
    }
  }
  const fetchSprayLogs = async (job) => {
    try {
      const response = await axios.get(`${url}/api/hmi/genericGetRequest`, {
        params: {
          type: 'SPRAY_LOGS_BY_WORKORDER',
          workorderId: job._id
        }
      });
      if (response.data?.length) {
        console.log('Got spray logs...', response.data)
        setActiveSprayLog(response.data[0])
      }
    } catch (e) {
      console.log('Error getting spray logs...', e)
    }
  }
  const fetchGPSData = async (job) => {
    console.log('fetchGPSData', job)
    let newGPSData = []
    try {
      // Format date for API
      // const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      
      // Replace with your actual API endpoint
      const response = await axios.get(`${url}/api/hmi/genericGetRequest`, {
        params: {
          type: 'GPS_DATA_BY_WORKORDER',
          workorderId: job._id
        }
      });
      console.log('GPS Activity and Service Visits...', response.data)
      // Set jobs from response
      if (response && response.data && response.data.data && response.data.data.gpsData.length) {
        newGPSData = response.data.data.gpsData
        // setJobs(response.data.data || []);
        // console.log('Selected Date', selectedDate)
        // filterOurJobs()
        // let jobsForToday = [...response.data.data].filter(wo => {
        //   console.log('Wo - completed on??', wo)
        //   const completedDate = wo.work_complete_at
        //   console.log('CompletedDat', completedDate)
        //   // const formattedCmpDate = format(completedDate, 'yyyy-MM-dd');
        //   // console.log('formattedCmpDate: %s . . formattedDate: %s ', formattedCmpDate, formattedDate)
        // }
        //   )
        //   console.log('jobsForToday', jobsForToday)
        // // setFilteredJobs(jobsForToday)
      }
      setGPSData(newGPSData)
    } catch (error) {
      console.error('Error fetching completed jobs:', error);
      // setJobs([]);
    } finally {
      // setLoading(false);
    }
  }

  const fetchPropertyLines = async (jobSite) => {
    console.log('fetchPropertyLines', jobSite)
    try {
      let siteId = jobSite
      if (jobSite?._id) siteId = jobSite._id
      const response = await axios.get(`${url}/api/customer/getAPNAndParcelInfo?id=${siteId}`);
      console.log("Property lines API response received.", response.data);
      let fetchedPropertyLines = []
     if (response?.data?.length) {
         fetchedPropertyLines = response.data
             .filter(property => property?.googleMapsFormat?.length > 0) // Ensure paths exist
             .map((property, index) => {
                 const parcelData = property.properties || {};
                 const paths = property.googleMapsFormat.map(p => ({ lat: p.lat, lng: p.lng })); // Ensure {lat, lng} format
                 return {
                     id: `prop_${siteId}_${parcelData.APN || index}`, // Create a more unique ID
                     APN: parcelData.APN,
                     address: parcelData.address,
                     paths: paths, // Use processed paths
                     type: 'propertyLine', // Standard type identifier
                     isHidden: false, // Default to visible
                     // Style properties are handled by MapComponent based on 'type'
                 };
             });
  
         if (fetchedPropertyLines.length > 0) {
             console.log(`Processed ${fetchedPropertyLines.length} property lines.`);
             showSnackbar('Property boundaries loaded', 'success');
         } else {
             console.log("No valid property boundary data found in API response (missing paths?).");
             showSnackbar('No property boundary data available', 'info');
         }

    }
    console.log('472...', fetchedPropertyLines)
    setPropertyLines(fetchedPropertyLines)
    } catch (e) {
      console.log('Error 442', e)
      setPropertyLines([])
    }
  }
  const handleJobClick = (job) => {
    setGPSData([])
    setPropertyLines([])
    setActiveSprayLog({})
    setSelectedJob(job);
    fetchGPSData(job)
    fetchSprayLogs(job)
    fetchPropertyLines(job?.jobSite)
    fetchWorkorderNotes(job)
    setOpenDialog(true);
    setTabValue(0); // Reset to first tab when opening dialog
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const formatTime = (timeString) => {
    try {
      const date = new Date(timeString);
      return format(date, 'h:mm a');
    } catch (error) {
      return timeString;
    }
  };

  const formatDuration = (startTime, stopTime) => {
    try {
      const start = new Date(startTime);
      const stop = new Date(stopTime);
      const diffMs = stop - start;
      const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMins = Math.round((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (diffHrs > 0) {
        return `${diffHrs}h ${diffMins}m`;
      } else {
        return `${diffMins}m`;
      }
    } catch (error) {
      return 'Unknown';
    }
  };


  const generateDescription = (weather) => {
    console.log('Get Weather Description', weather)
    let desc = ''
    if (!weather) return desc
    weather?.weather?.forEach(item => {
      desc += ` ${item.description}`
    })
    return desc
    // 
  }
  console.log('selectedJob....', selectedJob)

  return (
    <div className={classes.root}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6">
              Completed Spray Jobs Dashboard
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<MapIcon />}
              component={Link}
              to="/herbicide-territories"
            >
              Manage Territories
            </Button>
          </Box>
          
          {/* Date Navigator */}
          <div className={classes.dateNavigator}>
            <Button 
              variant="outlined" 
              onClick={handlePreviousDay}
              className={classes.navButton}
              startIcon={<ChevronLeftIcon />}
            >
              Previous
            </Button>
            
            <Typography variant="h6" className={classes.dateDisplay}>
              <CalendarIcon className={classes.calendarIcon} />
              {isToday(selectedDate) ? 'Today' : format(selectedDate, 'MMMM d, yyyy')}
            </Typography>
            
            <Button 
              variant="outlined" 
              onClick={handleNextDay}
              className={classes.navButton}
              endIcon={<ChevronRightIcon />}
            >
              Next
            </Button>
          </div>
          
          {/* Jobs Grid */}
          {loading ? (
            <div className={classes.loadingContainer}>
              <CircularProgress />
            </div>
          ) : filteredJobs.length > 0 ? (
            <Grid container spacing={3}>
              {filteredJobs.map((job) => (
                <Grid item xs={12} sm={6} md={4} key={job._id}>
                  <Card 
                    className={classes.jobCard}
                    onClick={() => handleJobClick(job)}
                  >
                    <CardContent className={classes.jobCardContent}>
                      <Typography variant="h6" className={classes.jobTitle}>
                        {job?.jobSite?.name}
                      </Typography>
                      <Typography variant="body2" className={classes.jobSubtitle}>
                        {job.jobName}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {job?.jobSite?.address}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                          {formatTime(job?.sprayOperation?.startTime)} - {formatTime(job?.sprayOperation?.stopTime)}
                        </Typography>
                      
                      <div className={classes.jobStatus}>
                        {/* <Typography variant="body2" color="textSecondary">
                          {job?.work_completed_by?.firstName}  {job?.work_completed_by?.lastName}
                        </Typography> */}
                        <div className={classes.completedByChip}> {job?.work_completed_by?.firstName}  {job?.work_completed_by?.lastName}</div>
                        <div className={classes.completedChip}>Completed</div>
                      </div>
                     
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper className={classes.noJobs}>
              <Typography variant="body1">
                No completed jobs found for this date
              </Typography>
            </Paper>
          )}
        </CardContent>
      </Card>

      {/* Job Details Dialog */}
      {selectedJob && (
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          fullWidth 
          maxWidth="lg"
          aria-labelledby="job-details-dialog"
        >
          <div className={classes.dialogTitle}>
            <DialogTitle id="job-details-dialog">
              {selectedJob.jobsiteName}
            </DialogTitle>
            <IconButton edge="end" color="inherit" onClick={handleCloseDialog} aria-label="close">
              <CloseIcon />
            </IconButton>
          </div>
         
          {/* Tabs for Map and Spray Details */}
          {/* <AppBar position="static" color="default" elevation={0}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab label="Map View" icon={<MapIcon />} />
              <Tab label="Spray Details" icon={<TimelineIcon />} />
            </Tabs>
          </AppBar> */}
          
          <DialogContent className={classes.dialogContent}>
              <Typography>{selectedJob?.jobName}</Typography>
              <Typography>{selectedJob?.jobSite?.address}{selectedJob.jobSite.buildingNumber ? ` Bldg #${selectedJob.jobSite.buildingNumber}` : ''} {selectedJob?.jobSite?.city}</Typography>
              <MapView 
                activeSprayLog={activeSprayLog}
                jobData={selectedJob} 
                woNotes={woNotes}
                propertyLines={propertyLines} 
                gpsData={gpsData } 
                chemicals={data.chemicals} />
            {/* Map Tab */}
            {/* <TabPanel value={tabValue} index={0}>
              {/* <div className={classes.mapContainer}>
                <div className={classes.mapPlaceholder}>
                  <MapIcon style={{ fontSize: 48, opacity: 0.5 }} />
                    <MapView jobData={selectedJob} />
            
                </div>
              </div>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    <MapIcon className={classes.sectionIcon} />
                    Location Information
                  </Typography>
                  
                  <Paper className={classes.locationInfo}>
                    <Typography variant="body2" className={classes.infoLabel}>
                      Start Location:
                    </Typography>
                    <Typography variant="body2" className={classes.infoValue}>
                      Lat: {selectedJob.location?.start?.lat}, Lng: {selectedJob.location?.start?.lng}
                    </Typography>
                    
                    <Divider style={{ margin: '8px 0' }} />
                    
                    <Typography variant="body2" className={classes.infoLabel}>
                      Stop Location:
                    </Typography>
                    <Typography variant="body2" className={classes.infoValue}>
                      Lat: {selectedJob.location?.stop?.lat}, Lng: {selectedJob.location?.stop?.lng}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Weather Conditions
                  </Typography>
                  
                  <div className={classes.weatherInfo}>
                    <div className={classes.weatherItem}>
                      <Typography variant="body2" className={classes.infoLabel}>Temperature:</Typography>
                      <Typography variant="body2">{selectedJob?.sprayOperation?.weatherData?.main?.temp}</Typography>
                    </div>
                    
                    <div className={classes.weatherItem}>
                      <Typography variant="body2" className={classes.infoLabel}>Wind Speed:</Typography>
                      <Typography variant="body2">{selectedJob?.sprayOperation?.weatherData?.wind?.speed}</Typography>
                    </div>
                    
                    <div className={classes.weatherItem}>
                      <Typography variant="body2" className={classes.infoLabel}>Wind Direction:</Typography>
                      <Typography variant="body2"><WindDirectionIcon degree={selectedJob?.sprayOperation?.weatherData?.wind?.deg} />
                      
                      </Typography>
                    </div>
                    
                    <div className={classes.weatherItem}>
                      <Typography variant="body2" className={classes.infoLabel}>Conditions:</Typography>
                      <Typography variant="body2">{generateDescription(selectedJob?.sprayOperation?.weatherData)}</Typography>
                    </div>
                  </div>
                </Grid>
              </Grid> */}
              {/* <MapView jobData={selectedJob} /> */}
            {/* </TabPanel> */} 
            
            {/* Spray Details Tab */}
            {/* <TabPanel value={tabValue} index={1}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    <TimelineIcon className={classes.sectionIcon} />
                    Time Details
                  </Typography>
                  
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell className={classes.infoLabel}>Start Time:</TableCell>
                          <TableCell>{formatTime(selectedJob.startTime)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.infoLabel}>Stop Time:</TableCell>
                          <TableCell>{formatTime(selectedJob.stopTime)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.infoLabel}>Duration:</TableCell>
                          <TableCell>{formatDuration(selectedJob.startTime, selectedJob.stopTime)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.infoLabel}>Technician:</TableCell>
                          <TableCell>{selectedJob.techName}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.infoLabel}>License #:</TableCell>
                          <TableCell>{selectedJob.licenseNum}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Chemicals Used
                  </Typography>
                  
                  {selectedJob.chemicalsUsed && selectedJob.chemicalsUsed.length > 0 ? (
                    <div>
                      {selectedJob.chemicalsUsed.map((chemical, index) => (
                        <div key={index} className={classes.chemicalItem}>
                          <Typography variant="body1">{chemical.name}</Typography>
                          <Typography variant="body2" color="textSecondary">
                            Amount: {chemical.amount} • Concentration: {chemical.concentration}
                          </Typography>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Typography variant="body2">No chemicals recorded for this job</Typography>
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    <NoteIcon className={classes.sectionIcon} />
                    Notes
                  </Typography>
                  
                  <Paper style={{ padding: '16px' }}>
                    {selectedJob.notes ? (
                      <Typography variant="body2">{selectedJob.notes}</Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary">No notes recorded for this job</Typography>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel> */}
          </DialogContent>
          
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default DashboardHeader;
